import { useState } from "react";
import type { Currency } from "@/api/types";
import { TextInput } from "./TextInput";
import { IconButton } from "./IconButton";
import { parseManualAmount } from "@/utils/manualReceipt";
import "./manual-line-draft-row.css";

const CCY_SYMBOL: Record<Currency, string> = {
  UAH: "₴",
  EUR: "€",
  USD: "$",
  PLN: "zł",
};

export interface ManualLineDraftRowProps {
  index: number;
  name: string;
  amount: string;
  qty: number;
  ccy: Currency;
  /** Removable = not the only row (can't remove the last one). */
  removable: boolean;
  /** Surface per-field errors only after a submit attempt (don't nag mid-typing). */
  showErrors: boolean;
  onName: (value: string) => void;
  onAmount: (value: string) => void;
  onQty: (value: number) => void;
  onRemove: () => void;
}

/**
 * A single client-side draft line inside <ManualReceiptForm> (components.md
 * §7.8). Holds no persisted server line — the row is turned into an inline
 * `AddLineRequest` only when the form is submitted. Name + amount are required;
 * quantity is optional (defaults to 1) and stays collapsed to a compact `× N`
 * chip until tapped, so it adds no visual weight to the common qty-1 case.
 */
export function ManualLineDraftRow({
  index,
  name,
  amount,
  qty,
  ccy,
  removable,
  showErrors,
  onName,
  onAmount,
  onQty,
  onRemove,
}: ManualLineDraftRowProps) {
  const [qtyOpen, setQtyOpen] = useState(false);

  const nameError =
    showErrors && name.trim() === "" && amount.trim() !== ""
      ? "Вкажи назву"
      : undefined;
  const amountError =
    showErrors && amount.trim() !== "" && parseManualAmount(amount) === null
      ? "Некоректна сума"
      : showErrors && name.trim() !== "" && amount.trim() === ""
        ? "Вкажи суму"
        : undefined;

  return (
    <div className="wp-mldr" data-testid="manual-line-row">
      <TextInput
        label={`Назва позиції ${index + 1}`}
        hideLabel
        className="wp-mldr__name"
        placeholder="Напр., Пляшка вина"
        value={name}
        error={nameError}
        onChange={(e) => onName(e.target.value)}
        data-testid="manual-line-name"
      />
      <div className="wp-mldr__row2">
        {qtyOpen ? (
          <div
            className="wp-mldr__stepper"
            role="group"
            aria-label={`Кількість, позиція ${index + 1}`}
          >
            <IconButton
              aria-label="Менше"
              onClick={() => onQty(Math.max(1, qty - 1))}
            >
              −
            </IconButton>
            <span className="wp-mldr__qty-val" data-testid="manual-line-qty">
              {qty}
            </span>
            <IconButton
              aria-label="Більше"
              onClick={() => onQty(Math.min(99, qty + 1))}
            >
              +
            </IconButton>
          </div>
        ) : (
          <button
            type="button"
            className="wp-mldr__qty-chip"
            onClick={() => setQtyOpen(true)}
            aria-label={`Кількість: ${qty}. Натисни щоб змінити.`}
            data-testid="manual-line-qty-chip"
          >
            × {qty}
          </button>
        )}
        <TextInput
          label={`Сума, позиція ${index + 1}`}
          hideLabel
          className="wp-mldr__amount"
          inputMode="decimal"
          placeholder="0,00"
          value={amount}
          error={amountError}
          suffix={CCY_SYMBOL[ccy]}
          onChange={(e) => onAmount(e.target.value)}
          data-testid="manual-line-amount"
        />
        {removable ? (
          <IconButton
            aria-label={`Видалити позицію ${index + 1}`}
            onClick={onRemove}
            data-testid="manual-line-remove"
          >
            ✕
          </IconButton>
        ) : null}
      </div>
    </div>
  );
}
